import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  login as loginService,
  logout as logoutService,
  onAuthStateChange,
  fetchUserProfile,
  writeStoredActiveViewId,
} from '../services/auth';
import {
  setActiveViewId,
  subscribeToLegalAcceptanceRequired,
  subscribeToOrganizationAwaitingPayment,
  subscribeToOrganizationPaused,
} from '../services/api';
import { parseLegalAcceptanceRequired } from '../services/legalAcceptanceRequired';
import type { AuthView, User, LoginRequest, AuthError, OrganizationPaymentTrial } from '../types/auth';
import type { LegalAcceptanceRequired } from '../types/legal';
import { SentryService } from '../services/sentry';
import { clearAllTeamChips } from '../utils/teamChipsStorage';
import { clearAllScheduleScopes } from '../utils/scheduleScopeStorage';
import { clearAllPageSizes } from '../utils/pageSizeStorage';
import { clearAllColumnLayouts } from '../utils/playersTableColumnStorage';
import { clearGetStartedChecklistCache } from '../services/onboarding';
import { invalidatePageCache as invalidatePlayerPages } from '../features/player-database/pagePrefetch';
import { clearStatFilterCatalogCache } from '../features/player-database/statFilterCatalogCache';

import { clearSignup } from '../features/signup/signupStorage';
import { clearTravelBallProgramSignup } from '../features/travel-ball/signup/tbProgramSignupStorage';
import { clearTravelBallFamilySignup } from '../features/travel-ball/signup/tbFamilySignupStorage';
import { clearFamilyPlayerSelections } from '../features/travel-ball/family/familyPlayerSelection';
import { applyBrandTheme } from '../utils/brandTheme';

/**
 * Player Database rows carry the program's own data, and the stat filter catalog follows its plan, so
 * both go whenever the user, organization or impersonation changes.
 */
const invalidatePageCache = (): void => {
  invalidatePlayerPages();
  clearStatFilterCatalogCache();
};

export type ActiveOrganization = {
  id: string;
  name: string;
  sport: string | null;
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  initializing: boolean;
  error: AuthError | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  // System admin org switching
  activeOrganization: ActiveOrganization | null;
  isSystemAdmin: boolean;
  isImpersonating: boolean;
  switchOrganization: (org: ActiveOrganization) => void;
  clearImpersonation: () => void;
  // The effective org ID being used for API calls (for use as a dependency)
  effectiveOrganizationId: string | null;
  // Organization pause (billing suspension) state
  organizationPaused: boolean;
  organizationPausedMessage: string | null;
  /**
   * The organization was created by self-serve signup and has not paid yet. `ProtectedRoute` sends
   * such a user to `/signup/payment` rather than into an app where every request is refused.
   */
  organizationAwaitingPayment: boolean;
  /**
   * The unpaid organization's free trial, which drives the trial banner. Null when there is none,
   * when the API did not send one, and for system admins (who are never asked to pay).
   */
  organizationPaymentTrial: OrganizationPaymentTrial | null;
  /**
   * The Terms of Service and/or Privacy Policy this user still has to accept. Non-null blocks the
   * app behind `LegalAcceptanceGate`. From the profile, or from a mid-session 403
   * `legal_acceptance_required` until a fresh profile replaces it.
   */
  legalAcceptanceRequired: LegalAcceptanceRequired | null;
  /**
   * Re-fetch /api/auth/me (reachable while paused or awaiting payment) to pick up a change. Resolves
   * with the user it stored, for a caller that must act on the fresh views before React re-renders.
   */
  refreshProfile: () => Promise<User>;
  /**
   * Phase 1b.5: the workspace this signed-in Firebase identity is operating in. Picked from
   * `user.views` using `user.activeViewId` (the one the server honoured for the bootstrap `/auth/me`
   * read). Null when the user has no views at all (true orphan), when the server did not pick a
   * view, or when no user is signed in. Switching requires calling `switchView` below so the
   * change writes through to localStorage and the request header before any subsequent fetch.
   */
  activeView: AuthView | null;
  /**
   * All workspaces this identity can enter. Empty when the user is signed in but has no
   * memberships (orphan), or when the API is older than Phase 1b.5 and did not send a listing.
   */
  views: AuthView[];
  /**
   * Pick a different workspace. Writes to localStorage keyed by Firebase uid, updates the active
   * view id on the api client (so the next request carries the new `X-Active-View` header), then
   * refreshes the profile so the server-confirmed shape of the new view is in context. A view id
   * the user does not actually own is rejected (returns false).
   */
  switchView: (viewId: string) => Promise<boolean>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/** A mid-session organization pause detected from a 403 organization_paused. */
type PausedOverride = { message: string | null };

const ACTIVE_ORG_STORAGE_KEY = 'activeOrganizationId';
const ACTIVE_ORG_NAME_STORAGE_KEY = 'activeOrganizationName';
const ACTIVE_ORG_SPORT_STORAGE_KEY = 'activeOrganizationSport';

/** How often, at most, coming back to the tab re-reads the profile while a free trial is shown. */
const TRIAL_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [activeOrganization, setActiveOrganization] = useState<ActiveOrganization | null>(null);
  /**
   * Set when an in-flight API call is rejected with a 403 organization_paused,
   * i.e. the org was paused after this session's profile was cached. Null means
   * "no mid-session pause detected"; the profile remains the source of truth.
   * The inner `message` is the server's copy, or null if it sent none.
   */
  const [pausedOverride, setPausedOverride] = useState<PausedOverride | null>(null);
  /**
   * Set when an API call is rejected with a 403 organization_awaiting_payment, the same way
   * `pausedOverride` is: the cached profile can say "active" while the server says otherwise (an
   * older profile, or a profile-less fallback user). Cleared by any fresh profile that is not
   * awaiting payment.
   */
  const [awaitingPaymentOverride, setAwaitingPaymentOverride] = useState(false);
  /**
   * Set from a 403 legal_acceptance_required body, so the dialog appears even before the profile
   * re-read it triggers comes back (or when that re-read fails). Any fresh profile replaces it.
   */
  const [legalOverride, setLegalOverride] = useState<LegalAcceptanceRequired | null>(null);
  /** The profile re-read a legal 403 started, so a burst of refusals asks /api/auth/me once. */
  const legalRefreshRef = useRef<Promise<void> | null>(null);
  // The account and program the Player Database page cache was last filled for; undefined before the first answer.
  const lastPageCacheScope = useRef<string | null | undefined>(undefined);

  // Derived values
  const isSystemAdmin = user?.profile?.role === 'system_admin';
  const isImpersonating = isSystemAdmin && activeOrganization !== null &&
    activeOrganization.id !== user?.profile?.organizationId;
  // The effective org ID - what API calls will use
  const effectiveOrganizationId = activeOrganization?.id || user?.profile?.organizationId || null;

  // Paused state: the profile from /api/auth/me, or a mid-session 403 override.
  // A profile-less user (offline fallback) reads as not paused, and system admins
  // are exempt from pausing entirely (the API never 403s them either).
  const organizationPaused =
    !isSystemAdmin &&
    (user?.profile?.organizationPaused === true || (user !== null && pausedOverride !== null));
  const organizationPausedMessage =
    pausedOverride?.message ?? user?.profile?.organizationPausedMessage ?? null;

  // Awaiting payment: same derivation as the pause. System admins are exempt (the API never
  // refuses them) and a signed-out session never awaits anything.
  const organizationAwaitingPayment =
    !isSystemAdmin &&
    (user?.profile?.organizationAwaitingPayment === true ||
      (user !== null && awaitingPaymentOverride));

  // The trial only ever informs (the banner); it never decides access. System admins are exempt
  // for the same reason they are exempt from awaiting payment.
  const organizationPaymentTrial = isSystemAdmin
    ? null
    : (user?.profile?.organizationPaymentTrial ?? null);

  // Pending legal acceptance: the profile, or a mid-session 403 the next profile read has not
  // replaced yet. Not exempt for system admins: the API asks everyone. The one exception is a
  // system admin viewing another organization: the API skips the gate there and refuses the
  // accept POST (403 impersonation_not_allowed), so showing the dialog would trap them.
  const legalAcceptanceRequired = user && !isImpersonating
    ? (user.profile?.legalAcceptanceRequired ?? legalOverride)
    : null;

  // The organization's brand colour becomes the app's primary colour; signed out, the default navy.
  const brandColor = user?.profile?.organizationBrandColor ?? null;
  useEffect(() => {
    applyBrandTheme(brandColor);
  }, [brandColor]);

  // React to an organization being paused mid-session
  useEffect(() => {
    return subscribeToOrganizationPaused((message) => {
      setPausedOverride((current) =>
        current && current.message === message ? current : { message }
      );
    });
  }, []);

  // React to a request refused because the organization has not paid yet
  useEffect(() => {
    return subscribeToOrganizationAwaitingPayment(() => {
      setAwaitingPaymentOverride(true);
    });
  }, []);

  // Load persisted active organization on mount
  useEffect(() => {
    const storedOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    const storedOrgName = localStorage.getItem(ACTIVE_ORG_NAME_STORAGE_KEY);
    const storedOrgSport = localStorage.getItem(ACTIVE_ORG_SPORT_STORAGE_KEY);

    if (storedOrgId && storedOrgName) {
      setActiveOrganization({
        id: storedOrgId,
        name: storedOrgName,
        sport: storedOrgSport || null,
      });
    }
  }, []);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setInitializing(false);

      // A different account (signed out or in from another tab, or a session that ended) must not see
      // the last one's cached Player Database rows, which carry its program's own data.
      const scope = user ? `${user.uid}|${user.profile?.organizationId ?? ''}` : null;
      if (scope !== lastPageCacheScope.current) {
        if (lastPageCacheScope.current !== undefined) invalidatePageCache();
        lastPageCacheScope.current = scope;
      }

      // A freshly fetched profile is authoritative - drop any stale pause override
      // (e.g. the org was resumed since the 403 that set it). Only a user that
      // actually carries a profile counts: the profile-less fallback user built
      // when /api/auth/me fails transiently says nothing about pause state, and
      // must not clear a pause we already know about.
      if (user === null || (user.profile != null && user.profile.organizationPaused !== true)) {
        setPausedOverride(null);
      }
      // Same rule for awaiting payment: only a real profile that says "paid" clears it.
      if (
        user === null ||
        (user.profile != null && user.profile.organizationAwaitingPayment !== true)
      ) {
        setAwaitingPaymentOverride(false);
      }
      // A real profile is authoritative about legal acceptance either way.
      if (user === null || user.profile != null) {
        setLegalOverride(null);
      }

      // Set Sentry user context when user logs in or state changes
      if (user) {
        SentryService.setUser({
          id: user.uid,
          email: user.email || undefined,
          role: user.profile?.role,
          orgId: user.profile?.organizationId,
        });
      } else {
        // Clear Sentry user context on logout
        SentryService.clearContext();
      }

      // Clear active organization if user is not a system admin
      if (!user || user.profile?.role !== 'system_admin') {
        setActiveOrganization(null);
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_ORG_NAME_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_ORG_SPORT_STORAGE_KEY);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true);
      setError(null);
      const user = await loginService(credentials);
      setUser(user);

      // Set Sentry user context on successful login
      SentryService.setUser({
        id: user.uid,
        email: user.email || undefined,
        role: user.profile?.role,
        orgId: user.profile?.organizationId,
      });
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      await logoutService();
      setUser(null);
      setPausedOverride(null);
      setAwaitingPaymentOverride(false);
      setLegalOverride(null);

      // Clear Sentry user context on logout
      SentryService.clearContext();

      // Clear all team chips from localStorage
      clearAllTeamChips();

      // Clear the persisted schedule scope so it never leaks across users/tenants
      clearAllScheduleScopes();

      // Same for the players-list page size and column layout
      clearAllPageSizes();
      clearAllColumnLayouts();

      // The Get Started checklist is cached in module scope rather than localStorage, so it
      // outlives the React tree but not the tab. Without this the next user to sign in here reads
      // the previous one's checklist, including whether *they* had hidden it.
      clearGetStartedChecklistCache();
      // Player Database rows carry this program's own data ("In your players").
      invalidatePageCache();

      // An unfinished signup's form lives in localStorage (shared across tabs); it must not be
      // offered to whoever signs in next.
      clearSignup();
      clearTravelBallProgramSignup();
      clearTravelBallFamilySignup();
      // The one-team coach signup was removed on 2026-10-30, but a browser may still hold its stored
      // form (names and team), so sign-out still drops that key.
      try {
        localStorage.removeItem('rosterpilot.travelBallSignup');
      } catch {
        // Blocked storage: nothing was stored.
      }
      // A parent/guardian's picked player is per user, but this browser may be shared.
      clearFamilyPlayerSelections();
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  /**
   * Re-fetch the profile from /api/auth/me. That endpoint stays reachable while
   * an organization is paused, so this is how a user recovers when their org is
   * resumed mid-session (otherwise the stale pause override would persist).
   */
  const refreshProfile = useCallback(async () => {
    const refreshedUser = await fetchUserProfile();
    setUser(refreshedUser);

    if (refreshedUser.profile?.organizationPaused !== true) {
      setPausedOverride(null);
    }
    if (refreshedUser.profile?.organizationAwaitingPayment !== true) {
      setAwaitingPaymentOverride(false);
    }
    if (refreshedUser.profile != null) {
      setLegalOverride(null);
    }
    return refreshedUser;
  }, []);

  // Phase 1b.5: derive active view + views list from the user object. `activeView` prefers the id
  // the server echoed back (`activeViewId`), falling back to the server's default. A dual-view
  // user whose stored id names a view they no longer have (a removed workspace) lands on their
  // default rather than on whichever view happens to be listed first.
  const views = useMemo<AuthView[]>(() => user?.views ?? [], [user?.views]);
  const activeView = useMemo<AuthView | null>(() => {
    if (!user || views.length === 0) return null;
    const byId = (id: string | null | undefined) => (id ? views.find((v) => v.id === id) : undefined);
    return byId(user.activeViewId) ?? byId(user.defaultViewId) ?? views[0] ?? null;
  }, [user, views]);

  const switchView = useCallback(
    async (viewId: string): Promise<boolean> => {
      const firebaseUid = user?.uid ?? null;
      if (!firebaseUid) return false;
      const match = views.find((v) => v.id === viewId);
      if (!match) return false;
      writeStoredActiveViewId(firebaseUid, viewId);
      setActiveViewId(viewId);
      // A view switch changes the workspace as much as an organization switch does, and this path
      // never reaches onAuthStateChange's scope check, so drop the caches here. Both carry the
      // workspace being left: the checklist's completion, and Player Database rows' "In your players".
      clearGetStartedChecklistCache();
      invalidatePageCache();
      // Confirm against the server: /auth/me re-reads the user under the new header, which both
      // validates the id and reshapes any view-dependent response fields (college-specific fields
      // populate only when the active view is college).
      const refreshed = await fetchUserProfile();
      setUser(refreshed);
      return true;
    },
    [user?.uid, views],
  );

  // A request refused because the user has not accepted the current terms (published mid-session).
  // Show the dialog straight from the 403's own copy of the documents, and re-read the profile so
  // the profile agrees. A burst of refusals shares one re-read; a failed one keeps the override.
  useEffect(() => {
    return subscribeToLegalAcceptanceRequired((raw) => {
      const parsed = parseLegalAcceptanceRequired(raw);
      if (parsed) setLegalOverride(parsed);

      if (legalRefreshRef.current) return;
      legalRefreshRef.current = refreshProfile()
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          legalRefreshRef.current = null;
        });
    });
  }, [refreshProfile]);

  // The trial banner counts nothing itself: it shows the server's days left from the last profile
  // read. A tab left open across days would keep the old count, and a notify-only program would
  // never see its trial end. While a trial is shown, re-read the profile when the coach comes back
  // to the tab, at most every few minutes. A failed read keeps what is on screen.
  const hasPaymentTrial = organizationPaymentTrial !== null;
  useEffect(() => {
    if (!hasPaymentTrial) return;
    let lastRefresh = Date.now();
    const refreshIfStale = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefresh < TRIAL_REFRESH_INTERVAL_MS) return;
      lastRefresh = Date.now();
      refreshProfile().catch(() => {});
    };
    window.addEventListener('focus', refreshIfStale);
    document.addEventListener('visibilitychange', refreshIfStale);
    return () => {
      window.removeEventListener('focus', refreshIfStale);
      document.removeEventListener('visibilitychange', refreshIfStale);
    };
  }, [hasPaymentTrial, refreshProfile]);

  const switchOrganization = useCallback((org: ActiveOrganization) => {
    if (!isSystemAdmin) {
      console.warn('Only system admins can switch organizations');
      return;
    }

    setActiveOrganization(org);
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, org.id);
    localStorage.setItem(ACTIVE_ORG_NAME_STORAGE_KEY, org.name);
    if (org.sport) {
      localStorage.setItem(ACTIVE_ORG_SPORT_STORAGE_KEY, org.sport);
    } else {
      localStorage.removeItem(ACTIVE_ORG_SPORT_STORAGE_KEY);
    }

    // The cached checklist belongs to the organization we are leaving. Completion is org-wide and
    // api.ts scopes every request by the impersonation header, but nothing here reloads the page —
    // so without this an admin switching to a brand-new program sees that program's setup reported
    // as already done, using the previous one's answer.
    clearGetStartedChecklistCache();
    // Player Database rows carry this program's own data ("In your players").
    invalidatePageCache();
  }, [isSystemAdmin]);

  const clearImpersonation = useCallback(() => {
    setActiveOrganization(null);
    localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_ORG_NAME_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_ORG_SPORT_STORAGE_KEY);

    // Dropping the impersonation changes the organization just as much as taking one on.
    clearGetStartedChecklistCache();
    // Player Database rows carry this program's own data ("In your players").
    invalidatePageCache();
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    initializing,
    error,
    login,
    logout,
    clearError,
    activeOrganization,
    isSystemAdmin,
    isImpersonating,
    switchOrganization,
    clearImpersonation,
    effectiveOrganizationId,
    organizationPaused,
    organizationPausedMessage,
    organizationAwaitingPayment,
    organizationPaymentTrial,
    legalAcceptanceRequired,
    refreshProfile,
    activeView,
    views,
    switchView,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
